import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'

import type { PlayerBackendProps, PlayerHandle } from '../types'

const COLD_SEEK_PRIME_TIMEOUT_MS = 4_000

type ColdSeekPrime = {
  generation: number
  originalMuted: boolean
  phase: 'starting' | 'playing' | 'pausing'
  sawPlay: boolean
  sawPlaying: boolean
}

// 原生 timeupdate 仅 ~4Hz，直接驱动进度条会一格一格地跳。
// 播放期间用 requestAnimationFrame（~60Hz）按墙钟时间从锚点外推 currentTime，
// 补出帧间的平滑中间值；原生 timeupdate 作为权威值每次回写时重设锚点校正漂移。
const HtmlVideoPlayer = forwardRef<PlayerHandle, PlayerBackendProps>(({ source, className, ...callbacks }, ref) => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const rafIdRef = useRef<number | null>(null)
  const fallbackFrameRafIdRef = useRef<number | null>(null)
  const frameCallbackIdRef = useRef<number | null>(null)
  const frameGenerationRef = useRef(0)
  const framePendingRef = useRef(false)
  const hasPresentedFrameRef = useRef(false)
  const primeRef = useRef<ColdSeekPrime | null>(null)
  const primeTimeoutRef = useRef<number | null>(null)
  const suppressNextPauseRef = useRef(false)
  const suppressedMutedValueRef = useRef<boolean | null>(null)
  const callbacksRef = useRef(callbacks)
  const [showPoster, setShowPoster] = useState(Boolean(source.poster))
  // 外推锚点：某墙钟时刻（performance.now）对应的视频时间；rAF 据此外推。
  const anchorRef = useRef<{ wallTime: number; videoTime: number } | null>(null)

  callbacksRef.current = callbacks

  // 以当前（或指定）视频时间重设锚点——播放开始 / seek / 每次原生 timeupdate 校正漂移时调用。
  const reanchor = (videoTime: number) => {
    anchorRef.current = { wallTime: performance.now(), videoTime }
  }

  const stopLoop = () => {
    if (rafIdRef.current !== null) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = null
    }
  }

  const cancelPendingFrame = () => {
    const video = videoRef.current
    if (video && frameCallbackIdRef.current !== null) {
      video.cancelVideoFrameCallback(frameCallbackIdRef.current)
      frameCallbackIdRef.current = null
    }
    if (fallbackFrameRafIdRef.current !== null) {
      cancelAnimationFrame(fallbackFrameRafIdRef.current)
      fallbackFrameRafIdRef.current = null
    }
    framePendingRef.current = false
  }

  const clearPrimeTimeout = () => {
    if (primeTimeoutRef.current !== null) {
      window.clearTimeout(primeTimeoutRef.current)
      primeTimeoutRef.current = null
    }
  }

  const finishPrime = (video: HTMLVideoElement) => {
    const prime = primeRef.current
    if (!prime) return
    clearPrimeTimeout()
    primeRef.current = null
    suppressedMutedValueRef.current = video.muted === prime.originalMuted ? null : prime.originalMuted
    video.muted = prime.originalMuted
  }

  const abortPrime = (video: HTMLVideoElement, generation: number, notify = true) => {
    const prime = primeRef.current
    if (!prime || prime.generation !== generation) return
    if (!video.paused) {
      suppressNextPauseRef.current = true
      video.pause()
    }
    finishPrime(video)
    cancelPendingFrame()
    if (notify) callbacksRef.current.onFramePresentationAborted?.(video.currentTime)
  }

  const commitPresentedFrame = (video: HTMLVideoElement, generation: number) => {
    if (generation !== frameGenerationRef.current || !framePendingRef.current) return
    cancelPendingFrame()
    frameGenerationRef.current += 1
    hasPresentedFrameRef.current = true
    const prime = primeRef.current
    if (prime?.generation === generation) {
      if (video.paused) {
        finishPrime(video)
      } else {
        prime.phase = 'pausing'
        video.pause()
      }
    }
    setShowPoster(false)
    callbacksRef.current.onFramePresented?.(video.currentTime)
  }

  // currentTime / timeupdate / seeked 都不保证画面已经提交给合成器。
  // 在 seek 前预约下一帧回调，使冷启动时保持封面，直到目标帧真正可见。
  const waitForPresentedFrame = (video: HTMLVideoElement) => {
    cancelPendingFrame()
    const generation = ++frameGenerationRef.current
    framePendingRef.current = true
    if (typeof video.requestVideoFrameCallback === 'function') {
      frameCallbackIdRef.current = video.requestVideoFrameCallback(() => {
        commitPresentedFrame(video, generation)
      })
    }
    return generation
  }

  const startColdSeekPrime = (video: HTMLVideoElement, generation: number) => {
    if (hasPresentedFrameRef.current) return
    const existingPrime = primeRef.current
    if (existingPrime) {
      existingPrime.generation = generation
      clearPrimeTimeout()
      primeTimeoutRef.current = window.setTimeout(
        () => {
          abortPrime(video, generation)
        },
        COLD_SEEK_PRIME_TIMEOUT_MS,
      )
      return
    }
    if (!video.paused) return

    const prime: ColdSeekPrime = {
      generation,
      originalMuted: video.muted,
      phase: 'starting',
      sawPlay: false,
      sawPlaying: false,
    }
    primeRef.current = prime
    suppressedMutedValueRef.current = video.muted ? null : prime.originalMuted
    video.preload = 'auto'
    video.muted = true
    primeTimeoutRef.current = window.setTimeout(
      () => {
        abortPrime(video, generation)
      },
      COLD_SEEK_PRIME_TIMEOUT_MS,
    )
    void video
      .play()
      .catch(() => abortPrime(video, generation))
  }

  const promotePrimeToPlayback = (video: HTMLVideoElement) => {
    const prime = primeRef.current
    if (!prime) return
    const { phase, sawPlay, sawPlaying } = prime
    finishPrime(video)
    if (phase === 'pausing') suppressNextPauseRef.current = true
    if (sawPlay) callbacksRef.current.onPlay?.()
    if (sawPlaying) {
      startLoop()
      callbacksRef.current.onPlaying?.()
    }
  }

  const commitFallbackFrame = (video: HTMLVideoElement) => {
    if (!framePendingRef.current) return
    const generation = frameGenerationRef.current
    const commitAfterPaint = () => {
      fallbackFrameRafIdRef.current = requestAnimationFrame(() => {
        commitPresentedFrame(video, generation)
      })
    }
    if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
      commitAfterPaint()
    } else {
      video.addEventListener(
        'loadeddata',
        () => {
          if (generation === frameGenerationRef.current) commitAfterPaint()
        },
        { once: true },
      )
    }
  }

  // 仅在真正推进播放时运行（playing 启动，waiting / pause / ended 停止），
  // 避免缓冲卡顿时外推越过实际播放位置。
  const startLoop = () => {
    const video = videoRef.current
    if (!video) return
    stopLoop()
    reanchor(video.currentTime)
    const tick = () => {
      const el = videoRef.current
      const anchor = anchorRef.current
      if (!el || el.paused || !anchor) {
        rafIdRef.current = null
        return
      }
      const elapsed = (performance.now() - anchor.wallTime) / 1000
      const estimated = anchor.videoTime + elapsed * el.playbackRate
      const clamped = Number.isFinite(el.duration) ? Math.min(estimated, el.duration) : estimated
      callbacks.onTimeUpdate?.(clamped)
      rafIdRef.current = requestAnimationFrame(tick)
    }
    rafIdRef.current = requestAnimationFrame(tick)
  }

  // PlayerBinding 只 attach 一次句柄；方法内通过 ref 取当前 video/callback，故保持句柄身份稳定。
  useImperativeHandle(
    ref,
    () => ({
      play: () => {
        const video = videoRef.current
        if (!video) return
        promotePrimeToPlayback(video)
        void video
          .play()
          .catch(() => undefined)
      },
      pause: () => {
        const video = videoRef.current
        if (!video) return
        const prime = primeRef.current
        if (prime) {
          abortPrime(video, prime.generation)
          callbacksRef.current.onPause?.()
          return
        }
        video.pause()
      },
      seek: time => {
        if (videoRef.current) {
          if (Math.abs(videoRef.current.currentTime - time) < 0.001) {
            callbacksRef.current.onTimeUpdate?.(time)
            return
          }
          // 原生 seeking 与帧回调在移动端可能乱序；在发出 currentTime 前先同步建立等待态。
          callbacksRef.current.onSeeking?.()
          const generation = waitForPresentedFrame(videoRef.current)
          videoRef.current.currentTime = time
          // 立即重设锚点，避免 rAF 用旧锚点把跳转后的位置又外推回去。
          reanchor(time)
          // 移动端从未播放时可能不为暂停 seek 启动解码；在当前用户手势内静音播放到目标帧再恢复暂停。
          startColdSeekPrime(videoRef.current, generation)
        }
      },
      setVolume: volume => {
        if (videoRef.current) {
          videoRef.current.volume = volume
        }
      },
      setMuted: muted => {
        if (videoRef.current) {
          suppressedMutedValueRef.current = null
          videoRef.current.muted = muted
        }
      },
      setPlaybackRate: rate => {
        if (videoRef.current) {
          videoRef.current.playbackRate = rate
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  useEffect(
    () => () => {
      const video = videoRef.current
      if (video && primeRef.current) abortPrime(video, primeRef.current.generation, false)
      clearPrimeTimeout()
      stopLoop()
      cancelPendingFrame()
      // cleanup 只通过 ref 取当前状态，且必须只在卸载时执行。
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [],
  )

  useEffect(
    () => {
      const video = videoRef.current
      if (video && primeRef.current) abortPrime(video, primeRef.current.generation, false)
      frameGenerationRef.current += 1
      cancelPendingFrame()
      hasPresentedFrameRef.current = false
      setShowPoster(Boolean(source.poster))
      // source 是唯一触发条件；其余状态均由 ref 取最新值。
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [source.poster, source.src],
  )

  // 禁用画中画的兜底层：属性被摘掉（DevTools）或经其他途径进了 PiP 时，进入即退出。
  // 用原生 addEventListener 而不是 JSX 事件属性——React 的合成事件里没有 PiP 这两个事件。
  useEffect(
    () => {
      const video = videoRef.current
      if (!video) return
      const exitPip = () => {
        void document
          .exitPictureInPicture?.()
          .catch(() => undefined)
      }
      video.addEventListener('enterpictureinpicture', exitPip)
      return () => video.removeEventListener('enterpictureinpicture', exitPip)
    },
    [],
  )

  return (
    <div className="relative h-full w-full">
      <video
        ref={videoRef}
        className={className}
        poster={source.poster}
        src={source.src}
        // 禁用画中画：PiP 会把画面搬进独立的原生窗口，DOM 水印盖不上去，等于绕过溯源水印。
        // 属性挡掉浏览器自带入口（右键菜单 / 全局媒体控制）并让 requestPictureInPicture() 直接抛错；
        // 属性被摘掉的情况由上面 useEffect 里的 enterpictureinpicture 兜底。
        disablePictureInPicture
        playsInline
        onCanPlay={e => callbacks.onCanPlay?.(e.currentTarget.currentTime)}
        onDurationChange={e => callbacks.onDurationChange?.(e.currentTarget.duration)}
        onEnded={() => {
          const video = videoRef.current
          if (video && primeRef.current) abortPrime(video, primeRef.current.generation)
          stopLoop()
          callbacks.onEnded?.()
        }}
        onError={e => {
          if (primeRef.current) abortPrime(e.currentTarget, primeRef.current.generation)
          callbacks.onError?.(e.currentTarget.error)
        }}
        onLoadedMetadata={e =>
          callbacks.onLoadedMetadata?.({
            width: e.currentTarget.videoWidth,
            height: e.currentTarget.videoHeight,
          })
        }
        onPause={e => {
          stopLoop()
          const prime = primeRef.current
          if (prime) {
            if (prime.phase === 'pausing') {
              finishPrime(e.currentTarget)
            } else {
              abortPrime(e.currentTarget, prime.generation)
            }
            return
          }
          if (suppressNextPauseRef.current) {
            suppressNextPauseRef.current = false
            return
          }
          callbacks.onPause?.()
        }}
        onPlay={() => {
          const prime = primeRef.current
          if (prime) {
            prime.sawPlay = true
            return
          }
          callbacks.onPlay?.()
        }}
        onPlaying={e => {
          const prime = primeRef.current
          if (prime) {
            prime.phase = 'playing'
            prime.sawPlaying = true
            if (typeof e.currentTarget.requestVideoFrameCallback !== 'function') {
              commitFallbackFrame(e.currentTarget)
            }
            return
          }
          waitForPresentedFrame(e.currentTarget)
          startLoop()
          callbacks.onPlaying?.()
        }}
        onSeeked={e => {
          callbacks.onSeeked?.(e.currentTarget.currentTime)
          if (typeof e.currentTarget.requestVideoFrameCallback !== 'function') {
            commitFallbackFrame(e.currentTarget)
          }
        }}
        onTimeUpdate={e => {
          // 原生 ~4Hz 权威值：校正 rAF 漂移；暂停态下也借此推进（如暂停时 seek）。
          reanchor(e.currentTarget.currentTime)
          callbacks.onTimeUpdate?.(e.currentTarget.currentTime)
        }}
        onVolumeChange={e => {
          const suppressedMutedValue = suppressedMutedValueRef.current
          if (suppressedMutedValue !== null) {
            if (!primeRef.current && e.currentTarget.muted === suppressedMutedValue) {
              suppressedMutedValueRef.current = null
              callbacks.onVolumeChange?.(e.currentTarget.volume, e.currentTarget.muted)
            }
            return
          }
          callbacks.onVolumeChange?.(e.currentTarget.volume, e.currentTarget.muted)
        }}
        onWaiting={e => {
          stopLoop()
          // 目标帧已完成且视频已暂停后，忽略移动端迟到的 waiting，避免重新打开永不结束的 loading。
          if (!e.currentTarget.paused || framePendingRef.current) callbacks.onWaiting?.()
        }}
      />
      {showPoster && source.poster && (
        <img
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 h-full w-full object-contain"
          draggable={false}
          src={source.poster}
        />
      )}
    </div>
  )
})

HtmlVideoPlayer.displayName = 'HtmlVideoPlayer'

export { HtmlVideoPlayer }
